<script setup lang="ts">
import { computed, inject, ref, shallowRef } from 'vue'
import i18n from '../../../../VueI18n'
import type { Map as MapboxMap } from 'mapbox-gl'
import { bbox as turfBbox } from '@turf/turf'
import { GeoImagesViewModelKey } from '../keys/GeoImagesViewModel.key'
import { GeoImageOverlayProvider } from '../providers/GeoImageOverlay.provider'
import { CLIP_BUFFER_METERS } from '../providers/FarmClipBuffer.provider'
import Breadcrumb from '../../../../components/ui/breadcrumb/Breadcrumb.vue'
import MapBoxMap from '../../../ui/map/MapBoxMap.vue'
import MapBoxSource from '../../../ui/map/MapBoxSource.vue'
import MapBoxLayer from '../../../ui/map/MapBoxLayer.vue'

const vm = inject(GeoImagesViewModelKey)!

const IMAGE_SOURCE_ID = 'geo-image-overlay'
const COVERAGE_SOURCE_ID = 'geo-image-coverage'
const EMPTY_COLLECTION = { type: 'FeatureCollection', features: [] }

const mapReady = ref(false)
const mapInstance = shallowRef<MapboxMap | null>(null)

/** TEMPORARIO (diagnostico): estado exibido no painel sobre o mapa. */
const diagnostico = ref<{ camadaAdicionada: boolean; erroMapbox: string }>({
	camadaAdicionada: false,
	erroMapbox: '',
})

const diagnosticoLinhas = computed(() => {
	const overlay = vm.imageOverlay.value
	const buffer = vm.clipBuffer.value
	const fazendaBbox = buffer?.features?.length
		? (turfBbox(buffer as never) as [number, number, number, number])
		: null

	const fmt = (b: [number, number, number, number] | null) =>
		b ? b.map((v) => v.toFixed(5)).join(', ') : '—'

	let sobrepoe = '—'
	if (overlay && fazendaBbox) {
		const [iw, is, ie, ineg] = overlay.bounds
		const [fw, fs, fe, fn] = fazendaBbox
		sobrepoe = iw < fe && ie > fw && is < fn && ineg > fs ? 'sim' : 'NÃO'
	}

	const km = (b: [number, number, number, number] | null) => {
		if (!b) return null
		const lat = ((b[1] + b[3]) / 2) * (Math.PI / 180)
		return {
			largura: (b[2] - b[0]) * 111.32 * Math.cos(lat),
			altura: (b[3] - b[1]) * 110.57,
		}
	}
	const kmImagem = km(overlay?.bounds ?? null)
	const kmFazenda = km(fazendaBbox)
	const bandas = vm.extractedBands.value
	const pixelsComDado = bandas
		? `${(GeoImageOverlayProvider.coverageRatio(bandas) * 100).toFixed(1)}%`
		: '—'

	const cobertura =
		kmImagem && kmFazenda
			? `${((kmImagem.largura * kmImagem.altura * 100) / (kmFazenda.largura * kmFazenda.altura)).toFixed(1)}%`
			: '—'

	return [
		['overlay gerado', overlay ? 'sim' : 'NÃO'],
		[
			'tamanho imagem',
			kmImagem ? `${kmImagem.largura.toFixed(2)} x ${kmImagem.altura.toFixed(2)} km` : '—',
		],
		['cobertura da fazenda', cobertura],
		['pixels com dado', pixelsComDado],
		['camada no mapa', diagnostico.value.camadaAdicionada ? 'sim' : 'NÃO'],
		['sobrepõe a fazenda', sobrepoe],
		['bounds imagem', fmt(overlay?.bounds ?? null)],
		['bounds fazenda', fmt(fazendaBbox)],
		['erro mapbox', diagnostico.value.erroMapbox || '—'],
	]
})

/**
 * O breadcrumb lista apenas as etapas anteriores: como formStep vale 2 aqui,
 * o proprio componente as marca como concluidas.
 */
const completedStep = computed(() => vm.formStep.value)

const farmSpec = computed(() => ({
	type: 'geojson' as const,
	data: (vm.farmMap.value?.fazenda ?? EMPTY_COLLECTION) as any,
}))

const bufferSpec = computed(() => ({
	type: 'geojson' as const,
	data: (vm.clipBuffer.value ?? EMPTY_COLLECTION) as any,
}))

/**
 * Contorno da area coberta pela imagem. Sem ele, uma imagem que cobre uma
 * fracao pequena da fazenda se confunde com o satelite e parece ausente.
 */
const imageBoundsSpec = computed(() => {
	const bounds = vm.imageOverlay.value?.bounds
	const data = bounds
		? {
				type: 'FeatureCollection',
				features: [
					{
						type: 'Feature',
						properties: { marcacao: 'imagem' },
						geometry: {
							type: 'Polygon',
							coordinates: [
								GeoImageOverlayProvider.toImageCoordinates(bounds).concat([
									GeoImageOverlayProvider.toImageCoordinates(bounds)[0],
								]),
							],
						},
					},
				],
			}
		: EMPTY_COLLECTION
	return { type: 'geojson' as const, data: data as any }
})

function enquadrarImagem() {
	const bounds = vm.imageOverlay.value?.bounds
	if (!bounds || !mapInstance.value) return
	mapInstance.value.fitBounds(bounds as any, { padding: 60 })
}

function enquadrarFazenda() {
	const alvo = vm.clipBuffer.value ?? vm.farmMap.value?.fazenda ?? null
	if (!alvo || !mapInstance.value) return
	mapInstance.value.fitBounds(turfBbox(alvo as never) as any, { padding: 24, maxZoom: 15 })
}

/** Presente so em raster de banda unica, onde a cor representa altura ou cota. */
const valueRange = computed(() => vm.imageOverlay.value?.valueRange ?? null)

const missingLayers = computed(() => {
	const missing: string[] = []
	if (!vm.hasFarmShape.value) missing.push(i18n.t('validacaoImagemCamadaFazenda') as string)
	if (!vm.imageOverlay.value || vm.imageRenderFailed.value) {
		missing.push(i18n.t('validacaoImagemCamadaImagem') as string)
	}
	return missing
})

/** So faz sentido quando as duas camadas existem, mas nao se encontram. */
const imageOutsideFarm = computed(
	() => Boolean(vm.imageOverlay.value) && vm.hasFarmShape.value && !vm.imageIntersectsFarm.value,
)

/**
 * O overlay do raster entra pela instancia do mapa: MapBoxSource so cobre
 * sources GeoJSON, e a imagem precisa de um source do tipo "image".
 */
function onMapLoad({ map }: { map: MapboxMap }) {
	const overlay = vm.imageOverlay.value
	const coverage = vm.imageCoverage.value

	// TEMPORARIO (diagnostico): sem isto, uma falha do Mapbox ao carregar o
	// data URI da imagem nao aparece em lugar nenhum — a camada simplesmente
	// nao pinta e o mapa segue funcionando.
	map.on('error', (event: any) => {
		const detalhe = event?.error?.message ?? String(event?.error ?? event)
		console.error('[GeoImageValidation] erro do Mapbox:', detalhe)
		diagnostico.value.erroMapbox = detalhe
		// Erros de tile do satelite nao contam: so os do source da imagem.
		if (event?.sourceId === IMAGE_SOURCE_ID) vm.imageRenderFailed.value = true
	})

	if (overlay) {
		try {
			map.addSource(IMAGE_SOURCE_ID, {
				type: 'image',
				url: overlay.dataUrl,
				coordinates: GeoImageOverlayProvider.toImageCoordinates(overlay.bounds) as any,
			})
			map.addLayer({
				id: IMAGE_SOURCE_ID,
				type: 'raster',
				source: IMAGE_SOURCE_ID,
				paint: { 'raster-opacity': 1 },
			})
			if (coverage) {
				// Mascara verde sobre os pixels com dado: acompanha o recorte real da
				// imagem, que costuma ser so os talhoes, em vez do retangulo do bbox.
				map.addSource(COVERAGE_SOURCE_ID, {
					type: 'image',
					url: coverage.dataUrl,
					coordinates: GeoImageOverlayProvider.toImageCoordinates(coverage.bounds) as any,
				})
				map.addLayer({
					id: COVERAGE_SOURCE_ID,
					type: 'raster',
					source: COVERAGE_SOURCE_ID,
					paint: { 'raster-opacity': 1 },
				})
			}

			diagnostico.value.camadaAdicionada = Boolean(map.getLayer(IMAGE_SOURCE_ID))
			if (!diagnostico.value.camadaAdicionada) vm.imageRenderFailed.value = true
			console.log('[GeoImageValidation] camada da imagem adicionada', {
				camada: diagnostico.value.camadaAdicionada,
				tamanhoDataUrlKb: Math.round(overlay.dataUrl.length / 1024),
				coordinates: GeoImageOverlayProvider.toImageCoordinates(overlay.bounds),
			})
		} catch (error) {
			console.error('[GeoImageValidation] falha ao adicionar a camada da imagem', error)
			diagnostico.value.erroMapbox = String(error)
			vm.imageRenderFailed.value = true
		}
	}

	const fitTarget = vm.clipBuffer.value ?? vm.farmMap.value?.fazenda ?? null
	if (fitTarget) {
		map.fitBounds(turfBbox(fitTarget as any) as any, { padding: 24, maxZoom: 15 })
	} else if (overlay) {
		map.fitBounds(overlay.bounds as any, { padding: 24, maxZoom: 15 })
	}

	mapInstance.value = map
	mapReady.value = true
}
</script>

<template>
	<div class="validation-overlay">
		<header class="validation-topbar">
			<Breadcrumb
				:value="completedStep"
				:crumbs="vm.breadcrumbCrumbs.value"
				transform="lowercase"
			/>

			<div class="validation-actions">
				<button
					class="validation-cancel"
					:disabled="vm.disableSaveButton.value"
					@click="vm.cancelValidation()"
				>
					{{ $t('cancel') }}
				</button>
				<button
					class="validation-confirm"
					:disabled="!vm.canConfirmValidation.value"
					@click="vm.confirmValidation()"
				>
					{{ $t('validacaoImagemConcluir') }}
				</button>
			</div>
		</header>

		<div class="validation-map">
			<div
				v-if="vm.isLoadingValidation.value"
				class="validation-status"
			>
				<i class="fas fa-spinner fa-spin" />
				<span>{{ $t('validacaoImagemPreparando') }}</span>
			</div>

			<MapBoxMap
				v-else
				@load="onMapLoad"
			>
				<template v-if="mapReady">
					<MapBoxSource
						id="farm-clip-buffer"
						:spec="bufferSpec"
					/>
					<MapBoxLayer
						:spec="{
							id: 'farm-clip-buffer-line',
							type: 'line',
							source: 'farm-clip-buffer',
							layout: { 'line-join': 'round', 'line-cap': 'round' },
							paint: { 'line-color': '#ffffff', 'line-width': 8, 'line-opacity': 0.45 },
						}"
					/>

					<MapBoxSource
						id="geo-image-bounds"
						:spec="imageBoundsSpec"
					/>
					<MapBoxLayer
						:spec="{
							id: 'geo-image-bounds-line',
							type: 'line',
							source: 'geo-image-bounds',
							paint: { 'line-color': '#f5a623', 'line-width': 2, 'line-dasharray': [3, 2] },
						}"
					/>

					<MapBoxSource
						id="farm-shape"
						:spec="farmSpec"
					/>
					<MapBoxLayer
						:spec="{
							id: 'farm-shape-line',
							type: 'line',
							source: 'farm-shape',
							layout: { 'line-join': 'round', 'line-cap': 'round' },
							paint: { 'line-color': '#ffffff', 'line-width': 3 },
						}"
					/>
				</template>
			</MapBoxMap>

			<div class="map-legend">
				<div class="map-legend__item">
					<i class="map-legend__swatch map-legend__swatch--image" />
					<span>
						<strong>{{ $t('validacaoImagemLegendaExtensao') }}</strong>
						{{ $t('validacaoImagemLegendaExtensaoDetalhe') }}
					</span>
				</div>
				<div
					v-if="valueRange"
					class="map-legend__item"
				>
					<i class="map-legend__swatch map-legend__swatch--ramp" />
					<span>
						<strong>{{ $t('validacaoImagemLegendaAltura') }}</strong>
						<span class="map-legend__scale">
							<span>{{ valueRange.min.toFixed(1) }}</span>
							<span class="map-legend__ramp" />
							<span>{{ valueRange.max.toFixed(1) }}</span>
						</span>
					</span>
				</div>
				<div
					v-else
					class="map-legend__item"
				>
					<i class="map-legend__swatch map-legend__swatch--coverage" />
					<span>
						<strong>{{ $t('validacaoImagemLegendaCobertura') }}</strong>
						{{ $t('validacaoImagemLegendaCoberturaDetalhe') }}
					</span>
				</div>
				<div class="map-legend__item">
					<i class="map-legend__swatch map-legend__swatch--farm" />
					<span>
						<strong>{{ $t('validacaoImagemLegendaFazenda') }}</strong>
						{{ $t('validacaoImagemLegendaFazendaDetalhe') }}
					</span>
				</div>
				<div class="map-legend__item">
					<i class="map-legend__swatch map-legend__swatch--buffer" />
					<span>
						<strong>{{
							$t('validacaoImagemLegendaBuffer', { metros: CLIP_BUFFER_METERS })
						}}</strong>
						{{ $t('validacaoImagemLegendaBufferDetalhe') }}
					</span>
				</div>
			</div>

			<!-- TEMPORARIO (diagnostico): remover junto com os console.log. -->
			<div
				v-if="!vm.isLoadingValidation.value"
				class="validation-debug"
			>
				<strong>diagnóstico da imagem</strong>
				<div
					v-for="[rotulo, valor] in diagnosticoLinhas"
					:key="rotulo"
					class="validation-debug__row"
				>
					<span>{{ rotulo }}</span>
					<span>{{ valor }}</span>
				</div>
				<div class="validation-debug__actions">
					<button @click="enquadrarImagem()">enquadrar imagem</button>
					<button @click="enquadrarFazenda()">enquadrar fazenda</button>
				</div>
			</div>

			<div
				v-if="!vm.isLoadingValidation.value && (missingLayers.length || imageOutsideFarm)"
				class="validation-warning"
			>
				<i class="fas fa-exclamation-circle" />
				<span>
					<template v-if="missingLayers.length">
						{{ $t('validacaoImagemAviso', { camadas: missingLayers.join(', ') }) }}
					</template>
					<template v-else>
						{{ $t('validacaoImagemForaDaFazenda') }}
					</template>
					{{ $t('validacaoImagemConcluirBloqueado') }}
				</span>
			</div>
		</div>
	</div>
</template>

<style scoped>
.validation-overlay {
	position: fixed;
	top: 2.5rem;
	left: 0;
	right: 0;
	bottom: 0;
	display: flex;
	flex-direction: column;
	background: var(--neutrals-neutral-white, #fefefe);
	z-index: 200;
}

.validation-topbar {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 16px;
	padding: 6px 16px;
	background: var(--neutrals-neutral-white, #fefefe);
	flex-shrink: 0;
}

.validation-actions {
	display: flex;
	align-items: center;
	gap: 16px;
}

.validation-cancel {
	/* A barra usa caixa baixa; o text-transform preserva a tradução de cada idioma. */
	text-transform: lowercase;
	font-size: 13px;
	color: var(--neutrals-neutral-black, #272728);
	padding: 4px 8px;
	background: transparent;
}

.validation-confirm {
	font-size: 13px;
	padding: 5px 12px;
	border-radius: var(--border-xs, 4px);
	color: var(--neutrals-neutral-black, #272728);
	background: var(--primary-primary, #95bb49);
	font-weight: 600;
}

.validation-cancel:disabled,
.validation-confirm:disabled {
	opacity: 0.5;
	cursor: not-allowed;
}

.validation-map {
	position: relative;
	flex: 1 1 auto;
	min-height: 0;
}

/* O root do MapBoxMap usa h-full, entao precisa de altura resolvida no pai. */
.validation-map :deep(.mapbox-map-root) {
	height: 100%;
	width: 100%;
}

.validation-status {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	height: 100%;
	color: var(--grays-gray-medium, #737576);
}

.validation-debug {
	position: absolute;
	top: 12px;
	left: 12px;
	z-index: 2;
	display: flex;
	flex-direction: column;
	gap: 2px;
	padding: 8px 12px;
	font-size: 11px;
	font-family: monospace;
	border-radius: var(--border-xs, 4px);
	background: rgba(254, 254, 254, 0.94);
	border: 1px solid var(--grays-gray-medium, #737576);
	color: var(--neutrals-neutral-black, #272728);
	max-width: min(420px, 60vw);
}

.validation-debug__row {
	display: flex;
	justify-content: space-between;
	gap: 16px;
}

.validation-debug__actions {
	display: flex;
	gap: 8px;
	margin-top: 6px;
}

.validation-debug__actions button {
	font-size: 11px;
	padding: 2px 8px;
	border-radius: var(--border-xs, 4px);
	border: 1px solid var(--grays-gray-medium, #737576);
	background: transparent;
	color: var(--neutrals-neutral-black, #272728);
}

.validation-debug__row span:last-child {
	overflow-wrap: anywhere;
	text-align: right;
}

.map-legend {
	position: absolute;
	right: 12px;
	bottom: 12px;
	z-index: 2;
	display: flex;
	flex-direction: column;
	gap: 8px;
	padding: 10px 12px;
	border-radius: var(--border-xs, 4px);
	/* Fundo escuro: as marcacoes brancas nao apareceriam sobre fundo claro. */
	background: rgba(39, 39, 40, 0.82);
	color: #fefefe;
	font-size: 11px;
	line-height: 1.3;
	max-width: min(260px, 46vw);
}

.map-legend__item {
	display: flex;
	align-items: flex-start;
	gap: 8px;
}

.map-legend__item span {
	display: flex;
	flex-direction: column;
}

.map-legend__item strong {
	font-weight: 600;
}

.map-legend__item span span,
.map-legend__item > span {
	color: rgba(254, 254, 254, 0.75);
}

.map-legend__item strong {
	color: #fefefe;
}

.map-legend__swatch {
	flex-shrink: 0;
	width: 18px;
	height: 12px;
	margin-top: 2px;
	border-radius: 2px;
	display: inline-block;
}

.map-legend__swatch--image {
	border: 2px dashed #f5a623;
}

.map-legend__swatch--coverage {
	background: rgba(27, 94, 32, 0.85);
}

/* Mesma rampa do provider: azul (baixo) -> vermelho (alto). */
.map-legend__swatch--ramp,
.map-legend__ramp {
	background: linear-gradient(
		to right,
		rgb(44, 123, 182),
		rgb(171, 217, 233),
		rgb(255, 255, 191),
		rgb(253, 174, 97),
		rgb(215, 25, 28)
	);
}

.map-legend__scale {
	display: flex;
	align-items: center;
	gap: 6px;
	margin-top: 2px;
}

.map-legend__ramp {
	flex: 1;
	min-width: 60px;
	height: 8px;
	border-radius: 2px;
	display: inline-block;
}

.map-legend__swatch--farm {
	border: 2px solid #ffffff;
}

.map-legend__swatch--buffer {
	border: 2px solid rgba(255, 255, 255, 0.45);
	border-style: solid;
	background: rgba(255, 255, 255, 0.15);
}

.validation-warning {
	position: absolute;
	left: 16px;
	bottom: 16px;
	right: 16px;
	max-width: 520px;
	display: flex;
	align-items: flex-start;
	gap: 8px;
	padding: 8px 12px;
	font-size: 12px;
	border-radius: var(--border-xs, 4px);
	background: rgba(254, 254, 254, 0.95);
	border: 1px solid var(--grays-gray-medium, #737576);
	color: var(--neutrals-neutral-black, #272728);
}
</style>
